import Form, { IChangeEvent } from '@rjsf/core';
import {
  AddButton,
  ArrayField,
  ArrayFieldItem,
  BaseInput,
  CheckboxWidget,
  ErrorList,
  Field,
  FieldError,
  MoveDownButton,
  MoveUpButton,
  RemoveButton,
  RteWidget,
  SelectWidget,
  SubmitButton,
  TextareaWidget,
  TitleField,
  Wrapper,
  GroupAccordion,
  SecretWidget,
} from '@/components/form';
import { GeoLocation } from '@/components/form/fields/geo-location';
import type {
  RegistryFieldsType,
  RegistryWidgetsType,
  ObjectFieldTemplateProps,
} from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { Fragment, useEffect, useState } from 'react';
import { useObjectFormUpsert } from '@/hooks/use-object-form-upsert';
import { useVaultItemFormUpsert } from '@/hooks/use-vault-item-form-upsert';
import { useUserNewOrganisationUserMutation } from '@/hooks/user/use-user-new-organisation-user';
import { useServiceAccountFormUpsert } from '@/hooks/use-service-account-form-upsert';

const fields: RegistryFieldsType = {
  geo: GeoLocation,
};

const widgets: RegistryWidgetsType = {
  CheckboxWidget,
  SelectWidget,
  TextareaWidget,
  rte: RteWidget,
  secret: SecretWidget,
};

interface UiGroup {
  title: string;
  template: 'accordion' | 'default';
  fields: string[];
}

export function EditForm({
  children,
  nodeFormData,
  schema,
  uiSchema,
  uuid,
  dataClassUuid,
  container,
  onSuccess,
  onUpsertStart,
  type
}: {
  children: (isLoading: boolean, error: string | null) => React.ReactNode;
  nodeFormData: any;
  schema: any;
  uiSchema: any;
  uuid: string;
  dataClassUuid: string;
  container: string;
  onSuccess: (data: any) => void;
  onUpsertStart: (formData: any) => void;
  type?: string;
}) {
  const objectFormUpsertMutation = useObjectFormUpsert();
  const vaultItemFormUpsertMutation = useVaultItemFormUpsert();
  const createOrganisationUserMutation = useUserNewOrganisationUserMutation();
  const serviceAccountFormUpsertMutation = useServiceAccountFormUpsert();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
  }, [uuid, dataClassUuid, container]);

  function handleUpsertError(upsertError: unknown) {
    setError(
      upsertError instanceof Error ? upsertError.message : 'Failed to save object'
    );
  }

  function handleObjectUpsert(event: IChangeEvent) {
    const transformedFormData = transformFormData(event);

    const objectData = {
      uuid,
      dataClassUuid,
      container,
      data: transformedFormData,
    };

    setError(null);
    onUpsertStart(transformedFormData);

    if (type === 'vault') {
      const vaultData = {
        dataClassUuid,
        key: uuid,
        data: transformedFormData,
      }

      return vaultItemFormUpsertMutation.mutate(vaultData, {
        onSuccess: onSuccess,
        onError: handleUpsertError,
      });
    }

    if (type === 'user') {
      if (dataClassUuid === "fd0cffa7-5d2d-48e5-aec7-44d8b4daac60") {
        return serviceAccountFormUpsertMutation.mutate(transformedFormData, {
          onSuccess: onSuccess,
          onError: handleUpsertError,
        });
      }

      return createOrganisationUserMutation.mutate(transformedFormData, {
        onSuccess: onSuccess,
        onError: handleUpsertError,
      });
    }

    objectFormUpsertMutation.mutate(objectData, {
      onSuccess: onSuccess,
      onError: handleUpsertError,
    });
  }

  return (
    <Form
      key={JSON.stringify(nodeFormData)}
      schema={schema}
      uiSchema={uiSchema}
      fields={fields}
      validator={validator}
      onSubmit={(data) => handleObjectUpsert(data)}
      formData={nodeFormData}
      templates={{
        ArrayFieldTemplate: ArrayField,
        ArrayFieldItemTemplate: ArrayFieldItem,
        BaseInputTemplate: BaseInput,
        ButtonTemplates: {
          AddButton,
          MoveDownButton,
          MoveUpButton,
          RemoveButton,
          SubmitButton,
        },
        ErrorListTemplate: ErrorList,
        FieldTemplate: Field,
        FieldErrorTemplate: FieldError,
        TitleFieldTemplate: TitleField,
        WrapIfAdditionalTemplate: Wrapper,
        ObjectFieldTemplate: ObjectFieldTemplateGrouped,
      }}
      widgets={widgets}
    >
      {children(
        objectFormUpsertMutation.isLoading ||
        vaultItemFormUpsertMutation.isLoading ||
        createOrganisationUserMutation.isLoading ||
        serviceAccountFormUpsertMutation.isLoading,
        error
      )}
    </Form>
  );
}

function ObjectFieldTemplateGrouped({
  properties,
  uiSchema,
}: ObjectFieldTemplateProps) {
  const grouped = new Set<string>();
  const groups = (uiSchema?.['ui:groups'] ?? []) as UiGroup[];

  const groupedFields = groups.map((group) => {
    if (!group.fields?.length || !group.title) {
      return null;
    }

    const children = properties
      .filter((p) => group.fields.includes(p.name))
      .sort((a, b) => group.fields.indexOf(a.name) - group.fields.indexOf(b.name));
    if (!children.length) {
      return null;
    }

    children.forEach((child) => grouped.add(child.name));

    return (
      <Fragment key={group.title}>
        {group.template === 'accordion' ? (
          <GroupAccordion
            name={group.title}
            children={children.map((child) => child.content)}
          />
        ) : (
          children.map((child) => child.content)
        )}
      </Fragment>
    );
  });

  const remaining = properties.filter((p) => !grouped.has(p.name));

  return (
    <fieldset>
      {groupedFields}
      {remaining.map((item) => (
        <Fragment key={item.name}>{item.content}</Fragment>
      ))}
    </fieldset>
  );
}

function transformFormData(event: IChangeEvent) {
  const updatedFormData = { ...event.formData };

  if (
    !event.uiSchema?.['ui:flatten'] ||
    event.uiSchema['ui:flatten'] !== true
  ) {
    return updatedFormData;
  }

  Object.keys(event.formData).forEach((key) => {
    if (key.startsWith('flat_')) {
      const flattenedKey = key.slice(5);
      const rawParts = flattenedKey.split('_');
      const parts = [];

      for (let i = 0; i < rawParts.length; i++) {
        if (rawParts[i] === '') {
          if (i + 1 < rawParts.length) {
            parts.push('_' + rawParts[i + 1]);
            i++;
          }
        } else {
          parts.push(rawParts[i]);
        }
      }

      let nestedObject = updatedFormData;
      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          nestedObject[part] = event.formData[key];
        } else {
          nestedObject[part] = nestedObject[part] || {};
        }
        nestedObject = nestedObject[part];
      });

      delete updatedFormData[key];
    }
  });

  return updatedFormData;
}
